import apiClient from "./apiClient";

// ─── Smart Learning Flow — FE helpers ─────────────────────────────────────
// Thin wrappers over /api/smart-learning. All endpoints are gated server-side
// by the smartLearningFlow feature flag; the FE checks the flag only to
// decide whether to render UI affordances (the Learning Path tab, etc.).

/**
 * Probe whether smartLearningFlow is enabled for the given course.
 * Returns `false` on any error so the UI gracefully hides the feature.
 */
export async function checkSmartLearningEnabled(courseId) {
  if (!courseId) return false;
  // Retry transient failures (network blip / momentary 5xx) so a single hiccup
  // doesn't make the caller hide the Smart Learning UI until the page is
  // reloaded. Only a definitive response flips the result.
  for (let attempt = 0; attempt < 3; attempt += 1) {
    try {
      const { data } = await apiClient.get("/smart-learning/feature-status", {
        params: { courseId },
      });
      return !!data?.enabled;
    } catch {
      if (attempt < 2) {
        await new Promise((r) => setTimeout(r, 500 * (attempt + 1)));
      }
    }
  }
  return false;
}

export async function fetchLearningPath({ courseId, batchId }) {
  const { data } = await apiClient.get("/smart-learning/learning-path", {
    params: { courseId, batchId },
  });
  return data;
}

export async function fetchSessionDetail(sessionId) {
  const { data } = await apiClient.get(`/smart-learning/sessions/${sessionId}`);
  return data; // { session, progress }
}

/**
 * Trainer/admin variant of the session-detail fetch. Hits the same endpoint but
 * passes the batch so the BE scopes engagement stats (submissions / attempts /
 * solves) to that batch's students. Returns { session, stats } — `session`
 * carries hydrated resources where each resource has `.stats` and `.staffHref`.
 */
export async function fetchTrainerSessionDetail(sessionId, batchId) {
  const { data } = await apiClient.get(`/smart-learning/sessions/${sessionId}`, {
    params: batchId ? { batchId } : {},
  });
  return data; // { session, stats }
}

/**
 * Resolve the Smart Learning session a recording belongs to (if any). Returns
 * `{ session, progress }` when the video maps to a session on a smart-learning
 * course, else `null`. Safe to call for every recording — the BE returns
 * `{ session: null }` when the flag is off or nothing matches.
 */
export async function fetchSessionByVideo(videoId) {
  if (!videoId) return null;
  try {
    const { data } = await apiClient.get(`/smart-learning/session-by-video/${videoId}`);
    return data?.session ? data : null; // { session, progress }
  } catch {
    return null;
  }
}

// ─── Session discussion (Q&A) ─────────────────────────────────────────────

export async function fetchSessionDiscussion(sessionId) {
  if (!sessionId) return [];
  const { data } = await apiClient.get(
    `/smart-learning/sessions/${sessionId}/discussion`,
  );
  return data?.messages || [];
}

export async function postSessionDiscussion(sessionId, { body, parentId } = {}) {
  const { data } = await apiClient.post(
    `/smart-learning/sessions/${sessionId}/discussion`,
    { body, parentId },
  );
  return data?.message || null;
}

export async function fetchGamification(courseId) {
  try {
    const { data } = await apiClient.get("/smart-learning/gamification", {
      params: courseId ? { courseId } : {},
    });
    return data; // { courses, summary }
  } catch {
    return null;
  }
}

export async function fetchContinueLearning() {
  try {
    const { data } = await apiClient.get("/smart-learning/continue-learning");
    return data?.cards || [];
  } catch {
    return [];
  }
}

export async function updateSessionProgress(sessionId, patch) {
  const { data } = await apiClient.post(
    `/smart-learning/sessions/${sessionId}/progress`,
    patch,
  );
  return data?.progress || null;
}

export async function startSmartSession(sessionId, payload = {}) {
  // payload shapes:
  //   { mode: 'auto', batchId }                                 — auto-create meeting
  //   { mode: 'manual', meetingJoinUrl, meetingId?, meetingProvider?, batchId? }
  const { data } = await apiClient.post(
    `/smart-learning/sessions/${sessionId}/start`,
    payload,
  );
  return data?.session || null;
}

export async function endSmartSession(sessionId) {
  const { data } = await apiClient.post(`/smart-learning/sessions/${sessionId}/end`);
  return data?.session || null;
}

/**
 * Manually reveal (or re-hide) a session's resources to students without ending
 * the class — the control for `unlockMode: 'manual'` sessions. Returns the
 * updated session.
 */
export async function setSessionResourcesUnlock(sessionId, unlocked = true) {
  const { data } = await apiClient.post(
    `/smart-learning/sessions/${sessionId}/resources-unlock`,
    { unlocked },
  );
  return data?.session || null;
}

/**
 * Restart (re-run) a completed session: the BE re-locks its resources, clears
 * this session's student progress, and moves it back to `scheduled`. Recordings
 * are kept (delete them separately). Returns { session, clearedProgress }.
 */
export async function restartSmartSession(sessionId) {
  const { data } = await apiClient.post(`/smart-learning/sessions/${sessionId}/restart`);
  return data;
}

/**
 * All recordings a session produced across its runs (each with a short-lived
 * signed URL for the in-app player). Trainer/admin only.
 */
export async function fetchSessionRecordings(sessionId) {
  if (!sessionId) return [];
  const { data } = await apiClient.get(`/smart-learning/sessions/${sessionId}/recordings`);
  return data?.recordings || [];
}

/** Soft-delete one of a session's recordings. Trainer/admin only. */
export async function deleteSessionRecording(sessionId, videoId) {
  await apiClient.delete(`/smart-learning/sessions/${sessionId}/recordings/${videoId}`);
}

/**
 * Fire a "I just completed this resource" signal. Safe to call even when the
 * resource isn't part of any Smart Learning session — the BE no-ops.
 * Errors are swallowed because this should never block the primary action.
 */
export async function emitResourceEvent({ kind, refId, score } = {}) {
  if (!kind || !refId) return;
  try {
    await apiClient.post("/smart-learning/resource-event", { kind, refId, score });
  } catch {
    /* fire-and-forget */
  }
}

/**
 * Heartbeat for a live session. Call every N seconds while the student is in
 * the meeting; deltaMinutes defaults to 1 so a 60s cadence accrues 1 min.
 */
export async function pingSessionAttendance(sessionId, deltaMinutes = 1) {
  if (!sessionId) return null;
  try {
    const { data } = await apiClient.post(
      `/smart-learning/sessions/${sessionId}/attendance/ping`,
      { deltaMinutes },
    );
    return data?.progress || null;
  } catch {
    return null;
  }
}

// ─── Admin authoring ──────────────────────────────────────────────────────

export async function adminListSessions({ courseId, moduleNumber, batchId } = {}) {
  const { data } = await apiClient.get("/smart-learning/admin/sessions", {
    // batchId → trainer/batch-scoped view (per-batch lifecycle, completed kept
    // visible). Omitted by the admin authoring view (course templates only).
    params: { courseId, moduleNumber, batchId },
  });
  return data; // { modules, sessions }
}

export async function adminCreateSession(payload) {
  const { data } = await apiClient.post("/smart-learning/admin/sessions", payload);
  return data?.session || null;
}

/**
 * Trainer/admin evaluation drill-down for one resource of a smart session.
 * Returns kind-specific data:
 *   quiz       → { questions:[…with correct answers], attempts:[…per student] }
 *   assignment → { submissions:[…], reviewHref }
 *   coding     → { submissions:[…] }
 */
export async function fetchSessionEvaluation(sessionId, { kind, refId, batchId } = {}) {
  const { data } = await apiClient.get(
    `/smart-learning/admin/sessions/${sessionId}/evaluation`,
    { params: { kind, refId, batchId } },
  );
  return data;
}

/**
 * Trainer/admin batch analytics dashboard (Analytics tab on the batch page).
 * Returns one payload: { codingEnabled, hasSmartSessions, kpis, modules[],
 * weakestTopics[], assignments[], quiz{hasData,…}, learnWithAI{hasData,…},
 * coding{…}|null, students[] }.
 */
export async function fetchBatchAnalytics(batchId) {
  const { data } = await apiClient.get(`/smart-learning/admin/batch/${batchId}/analytics`);
  return data;
}

/**
 * Trainer attaches an extra document/assignment to a smart session.
 * scope: 'batch' (this batch only) | 'all' (all upcoming batches).
 */
export async function addTrainerSessionResource(sessionId, { kind, refId, title, scope, batchId } = {}) {
  const { data } = await apiClient.post(
    `/smart-learning/admin/sessions/${sessionId}/trainer-resource`,
    { kind, refId, title, scope, batchId },
  );
  return data;
}

export async function adminUpdateSession(id, patch) {
  const { data } = await apiClient.put(`/smart-learning/admin/sessions/${id}`, patch);
  return data?.session || null;
}

export async function adminDeleteSession(id) {
  await apiClient.delete(`/smart-learning/admin/sessions/${id}`);
}

export async function adminReorderSessions({ courseId, moduleNumber, orderedIds }) {
  await apiClient.post("/smart-learning/admin/sessions/reorder", {
    courseId, moduleNumber, orderedIds,
  });
}

export async function adminSearchResources({
  kind, q, courseId, batchId, limit,
  status, mode, difficulty, category, label,
} = {}) {
  const { data } = await apiClient.get("/smart-learning/admin/resource-picker", {
    params: {
      kind, q, courseId, batchId, limit,
      status, mode, difficulty, category, label,
    },
  });
  // Returns { items, facets }. facets carries the distinct filter values for
  // this kind (e.g. categories/labels for coding, batches for assignments).
  return { items: data?.items || [], facets: data?.facets || {} };
}

/**
 * Upload a brand-new course-level document and return it as a pickable resource.
 * Used by the smart-session document picker's "Upload new" mode so admins can
 * attach a fresh material without leaving the editor. Smart sessions are
 * course-wide, so the material is stored at course level.
 */
export async function uploadSessionDocument({ courseId, title, file } = {}) {
  const form = new FormData();
  form.append("file", file);
  form.append("level", "course");
  form.append("courseId", courseId);
  form.append("title", title);
  form.append("fileName", file?.name || title);
  const { data } = await apiClient.post("/materials/upload", form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  const material = data?.data?.material;
  return material ? { _id: material._id, title: material.title || material.fileName } : null;
}

/**
 * Upload a standalone SCORM package for a smart session and return it as a
 * pickable resource. Reuses the SCORM admin upload pipeline (extraction runs in
 * the background worker); we pass `standalone: true` and no moduleId so the
 * package is course-scoped rather than placed in the self-paced curriculum.
 * Returns { _id, title } for the created SCORM Lesson, or null.
 */
export async function uploadSessionScorm({ courseId, title, file }, opts = {}) {
  const form = new FormData();
  form.append("file", file);
  form.append("courseId", courseId);
  form.append("title", title);
  form.append("standalone", "true");
  const { data } = await apiClient.post("/scorm/admin/upload", form, {
    headers: { "Content-Type": "multipart/form-data" },
    onUploadProgress: opts.onUploadProgress,
  });
  const lesson = data?.lesson;
  return lesson ? { _id: lesson._id, title: lesson.title } : null;
}

/**
 * Create a quiz inline for a smart session. The BE builds real QuizQuestion
 * bank docs + a published, course-assigned Assessment (the same exam engine
 * students take) and returns a pickable resource ref { _id, title, kind:'quiz' }.
 * `payload` matches QuizBuilder.buildPayload(): { courseId, title, passingScore,
 * timeLimit, maxAttempts, questions:[...] }.
 */
export async function adminCreateQuickQuiz(payload) {
  const { data } = await apiClient.post("/smart-learning/admin/quick-quiz", payload);
  return data?.resource || null;
}

/**
 * Load a session quiz (Assessment) back into the QuizBuilder shape for inline
 * editing. Returns { editable, quiz } for simple quizzes, or { editable:false,
 * summary } for complex/sectioned assessments (which open the full editor).
 */
export async function adminGetQuickQuiz(assessmentId) {
  const { data } = await apiClient.get(`/smart-learning/admin/quick-quiz/${assessmentId}`);
  return data; // { editable, quiz?, summary? }
}

/**
 * Update a session quiz inline from the QuizBuilder payload (edit + republish).
 * Returns the pickable resource ref { _id, title, kind:'quiz' }.
 */
export async function adminUpdateQuickQuiz(assessmentId, payload) {
  const { data } = await apiClient.put(`/smart-learning/admin/quick-quiz/${assessmentId}`, payload);
  return data?.resource || null;
}

/**
 * Update an existing session assignment (GeneralAssignment) inline. Multipart so
 * new attachments can be appended; existing attachments are preserved server-side.
 */
export async function updateSessionAssignment(assignmentId, formData) {
  const { data } = await apiClient.put(`/general-assignments/${assignmentId}`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data?.assignment || null;
}

/**
 * Generate draft MCQ questions for a session quiz with AI. `payload` carries the
 * session context (module/session title + description + optional topic) and the
 * admin's model pick (`model`, `reasoningEffort`) from GET
 * /smart-learning/admin/quiz-ai-models; omit those and the BE picks the
 * catalogue default. Pass `existingQuestions` (texts already in the builder) so
 * "generate more" never repeats and the total stays within the 20-question cap.
 * Returns an array in QuizBuilder shape.
 */
export async function adminGenerateQuizQuestions(payload) {
  const { data } = await apiClient.post("/smart-learning/admin/quiz-ai-generate", payload);
  return data?.questions || [];
}

/**
 * Create a GeneralAssignment inline (multipart) and return it. Assignments are
 * batch-scoped, so the FormData must carry batchId, dueDate and maxMarks
 * (required by the BE) alongside title / description / attachments[].
 */
export async function createSessionAssignment(formData) {
  const { data } = await apiClient.post("/general-assignments/add", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data?.assignment || null;
}

export async function adminToggleCourseFeature(courseId, enabled) {
  const { data } = await apiClient.post(
    `/smart-learning/admin/courses/${courseId}/feature-toggle`,
    { enabled: !!enabled },
  );
  return data;
}

// ─── Sessions generated from the syllabus ─────────────────────────────────
// How a course's sessions are formed (from the syllabus, or authored one by
// one), the rules every generated session follows, and the resources attached
// to the syllabus's topics.

export async function getCourseSmartSettings(courseId) {
  const { data } = await apiClient.get(`/smart-learning/admin/courses/${courseId}/smart-settings`);
  return data?.settings || null;
}

export async function saveCourseSmartSettings(courseId, patch) {
  const { data } = await apiClient.put(`/smart-learning/admin/courses/${courseId}/smart-settings`, patch);
  return data; // { settings, updatedSessions }
}

export async function listTopicResources(courseId) {
  const { data } = await apiClient.get(`/smart-learning/admin/courses/${courseId}/topic-resources`);
  return data?.resources || [];
}

export async function addTopicResource(courseId, { topicKey, kind, refId, title, contentType, durationMinutes }) {
  const { data } = await apiClient.post(`/smart-learning/admin/courses/${courseId}/topic-resources`, {
    topicKey, kind, refId, title, contentType, durationMinutes,
  });
  return data?.resource || null;
}

export async function updateTopicResource(id, patch) {
  const { data } = await apiClient.patch(`/smart-learning/admin/topic-resources/${id}`, patch);
  return data?.resource || null;
}

export async function deleteTopicResource(id) {
  await apiClient.delete(`/smart-learning/admin/topic-resources/${id}`);
}

// ─── State display helpers ────────────────────────────────────────────────

export const SESSION_STATES = {
  locked: { label: "Locked", tone: "muted" },
  available: { label: "Start", tone: "primary" },
  live: { label: "Live now", tone: "live" },
  in_progress: { label: "In progress", tone: "info" },
  completed: { label: "Completed", tone: "success" },
  mastered: { label: "Mastered", tone: "mastery" },
};

export function getStateMeta(state) {
  return SESSION_STATES[state] || SESSION_STATES.available;
}
